<template>
  <RevCard class="p-24">
    <div class="mb-24 flex items-center justify-between">
      <div class="heading-2 flex items-center gap-8">
        <IconShoppingBag />
        {{ i18n(translations.opportunities) }}
      </div>

      <div class="flex items-center gap-24">
        <RevButton
          size="small"
          :to="{ name: ROUTE_NAMES.OPPORTUNITIES.INDEX }"
          variant="secondary"
        >
          {{ i18n(translations.viewDetails) }}
        </RevButton>
      </div>
    </div>

    <div>
      <RevLoadingScreen
        v-if="isLoading"
        class="mt-32"
        :text="i18n(translations.loading)"
      />

      <div v-else class="space-y-12">
        <template v-for="metric in metrics?.pricing" :key="metric.code">
          <div
            v-if="METRIC_CODE_TRANSLATIONS[metric.code]"
            class="border-static-default-mid rounded-sm w-full border p-24"
          >
            <RevTag
              :label="
                i18n(METRIC_CODE_TRANSLATIONS[metric.code], {
                  count: metric.count,
                })
              "
              size="large"
              :variant="metric.count === 0 ? 'success' : 'info'"
            />

            <div>
              {{
                metric.count === 0
                  ? i18n(translations.noActionsDescription)
                  : i18n(METRIC_ACTION_TRANSLATIONS[metric.code], {
                      count: metric.count,
                      threshold: metric.threshold || DEFAULT_METRIC_THRESHOLD,
                    })
              }}
            </div>
          </div>
        </template>
      </div>
    </div>
  </RevCard>
</template>

<script setup lang="ts">
import { type Error, getApiErrorMessage } from '#imports'

import { sellerOpportunities } from '@backmarket/http-api'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import type { I18nDefinition } from '@backmarket/nuxt-module-i18n/types'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevCard } from '@ds/components/Card'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'
import { RevTag } from '@ds/components/Tag'
import { IconShoppingBag } from '@ds/icons/IconShoppingBag'

import { ROUTE_NAMES } from '~/constants/routes'

import translations from './Opportunities.translations'

const METRIC_CODE_TRANSLATIONS: Record<string, I18nDefinition> = {
  SMALL_EFFORT: translations.smallEffort,
  COLD: translations.cold,
  BACKBOX_TO_BOOST: translations.backboxToBoost,
}

const METRIC_ACTION_TRANSLATIONS: Record<string, I18nDefinition> = {
  SMALL_EFFORT: translations.smallEffortAction,
  COLD: translations.coldAction,
  BACKBOX_TO_BOOST: translations.backboxToBoostAction,
}

const DEFAULT_METRIC_THRESHOLD = 2

const i18n = useI18n()
const { openErrorToast } = useTheToast()

const {
  data: metrics,
  error,
  pending: isLoading,
} = await useHttpFetch(sellerOpportunities.getPricingOpportunitiesMetrics)

if (error.value) {
  openErrorToast({
    content: getApiErrorMessage(error.value as unknown as Error),
  })
}
</script>
